import { useEffect, useMemo, useRef, useState } from 'react';

import { ColorVariantOption } from './ColorVariantOption';

export function ColorVariantOptions({
  enabledColorNameOnHover,
  product,
  setVariantFromColor,
  swatchesMap,
  variantFromColor,
}) {
  const [maxCount, setMaxCount] = useState(14);

  const swatchListRef = useRef(null);

  const originalColor = product.optionsMap.Color?.[0];
  const colorOptions = product.grouping?.isTransformed
    ? [
        originalColor,
        ...product.grouping.optionsMap.Color.filter(
          (color) => color !== originalColor
        ),
      ]
    : product.optionsMap.Color;
  const slicedColorOptions = colorOptions.slice(0, maxCount);

  const variantMapByColor = useMemo(() => {
    const variants = product.grouping?.isTransformed
      ? product.grouping.variants
      : product.variants;
    return variants.reduce((acc, variant) => {
      const variantColor = variant.selectedOptionsMap?.Color;
      if (acc[variantColor]) return acc;
      return {
        ...acc,
        [variantColor]: variant,
      };
    }, {});
  }, [product.id]);

  useEffect(() => {
    function updateMaxCount() {
      if (!swatchListRef.current) return;

      // Snag the first swatch if we have it to calculate sizes
      const firstSwatch = swatchListRef.current.querySelector('li');
      if (!firstSwatch) return;

      // Calculate how many swatches fit per row based on the first swatch width and container width
      const swatchGap = 10;
      const swatchWidth = firstSwatch.offsetWidth + swatchGap;
      const containerWidth =
        swatchListRef.current.offsetWidth - swatchWidth + swatchGap;
      const count = Math.floor(containerWidth / swatchWidth);
      const remaining = colorOptions.length - count;

      // Remove existing "+N" element if present
      const existingRemaining =
        swatchListRef.current.querySelector('li.remaining');
      if (existingRemaining) {
        swatchListRef.current.removeChild(existingRemaining);
      }

      // If there are remaining swatches, show "+N"
      if (remaining > 0) {
        const listElement = document.createElement('li');
        listElement.className = 'remaining order-1 mr-auto text-[14px]';
        listElement.innerHTML = `+ ${remaining}`;
        swatchListRef.current.appendChild(listElement);
      }
      setMaxCount(count);
    }

    updateMaxCount();
    window.addEventListener('resize', updateMaxCount);
    return () => window.removeEventListener('resize', updateMaxCount);
  }, []);

  return (
    <ul ref={swatchListRef} className="flex w-full flex-wrap gap-[10px]">
      {slicedColorOptions.map((color, index) => {
        return (
          <li key={index}>
            <ColorVariantOption
              color={color}
              enabledColorNameOnHover={enabledColorNameOnHover}
              onClick={() => setVariantFromColor(variantMapByColor[color])}
              onMouseEnter={() => setVariantFromColor(variantMapByColor[color])}
              swatchesMap={swatchesMap}
              variantFromColor={variantFromColor}
            />
          </li>
        );
      })}
    </ul>
  );
}

ColorVariantOptions.displayName = 'ColorVariantOptions';
